// Activo Fijo → Configuración → Parámetros (fila única por empresa).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, CONVENCION, METODOS, ParametrosAF as TParam, REVAL_TECNICA, af, fieldCls } from './shared';

export function ParametrosAF() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<TParam[]>({
    queryKey: ['af-parametros'],
    queryFn: () => af.list<TParam>('parametros', '?select=*&limit=1'),
  });
  const actual = rows[0];
  const [form, setForm] = useState<TParam | null>(null);
  useEffect(() => { if (actual) setForm(actual); }, [actual]);
  const set = (p: Partial<TParam>) => setForm((f) => (f ? { ...f, ...p } : f));

  const guardar = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Sin datos');
      const body = {
        metodo_default: form.metodo_default, convencion_parcial: form.convencion_parcial,
        umbral_capitalizacion: Number(form.umbral_capitalizacion) || 0,
        reval_tecnica: form.reval_tecnica,
        tarifa_impuesto_renta_pct: Number(form.tarifa_impuesto_renta_pct) || 0,
      };
      return af.update('parametros', form.id, body);
    },
    onSuccess: () => { toast.success('Parámetros guardados'); void qc.invalidateQueries({ queryKey: ['af-parametros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isFetching || !form) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Settings className="w-5 h-5" /> Parámetros de Activo Fijo</h1>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Depreciación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Método por defecto"><select className={fieldCls} value={form.metodo_default} onChange={(e) => set({ metodo_default: e.target.value })}>{METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></Campo>
            <Campo label="Convención del mes de alta/baja"><select className={fieldCls} value={form.convencion_parcial} onChange={(e) => set({ convencion_parcial: e.target.value })}>{CONVENCION.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select></Campo>
          </div>
          <Campo label="Monto mínimo para capitalizar (umbral)">
            <input type="number" step="0.01" min="0" className={`${fieldCls} w-40`} value={form.umbral_capitalizacion} onChange={(e) => set({ umbral_capitalizacion: Number(e.target.value) })} />
          </Campo>
          <p className="text-xs text-muted-foreground">Compras por debajo del umbral se llevan a gasto en vez de capitalizarse como activo.</p>
          <p className="text-xs text-muted-foreground">La convención también decide el mes de SALIDA: con «mes completo», el mes de alta deprecia entero y el de baja no deprecia; con «prorrateo por días», los dos extremos se reparten por días.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Revaluación e impuesto diferido</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Campo label="Técnica de revaluación (NIC 16 §35)">
            <select className={fieldCls} value={form.reval_tecnica} onChange={(e) => set({ reval_tecnica: e.target.value })}>
              {REVAL_TECNICA.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Campo>
          <p className="text-xs text-muted-foreground">Las dos dejan el mismo valor en libros; cambia cómo quedan el costo bruto y la depreciación acumulada en el cuadro de movimiento del activo fijo. «Proporcional» conserva la edad del activo; «eliminación» es lo habitual en avalúos de inmuebles.</p>

          <Campo label="Tarifa de impuesto a la renta (%)">
            <input type="number" step="0.01" min="0" max="99.99" className={`${fieldCls} w-32`}
              value={form.tarifa_impuesto_renta_pct}
              onChange={(e) => set({ tarifa_impuesto_renta_pct: Number(e.target.value) })} />
          </Campo>
          <p className="text-xs text-muted-foreground">
            En <b>0</b> el módulo NO calcula impuesto diferido — es una decisión contable, no un valor que convenga heredar por defecto.
            Con una tarifa configurada, el superávit por revaluación genera su pasivo por impuesto diferido contra el patrimonio (NIC 12 §61A),
            y se habilita el cálculo anual por la diferencia entre depreciación contable y fiscal.
          </p>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">La numeración de placas se configura en Configuración → Numeración de placas.</p>
      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar cambios</Button>
      </div>
    </div>
  );
}
